/**
 * The source catalog: every metric, breakdown, table and forecast series a
 * widget can point at, assembled from the per-domain files plus the
 * org's own tracked KPIs (`kpi_definitions`), and filtered by what the
 * viewer is allowed to read.
 *
 * Widgets store catalog ids. A widget whose id is missing from the
 * viewer's catalog — retired source, or a finance tile shared with a
 * manager — renders an "unavailable" card rather than fetching anything.
 */

import { canRead, type Role, type SectionAccess } from '@/lib/permissions';
import type { BreakdownDef, ForecastSourceDef, MetricDef, SourceDef, TableDef } from './catalogTypes';
import { DATASETS, type DatasetBag, type DatasetId, type KpiDefinitionLite } from './datasets';
import type { DashboardWidget, Unit } from './types';
import { FINANCE_BREAKDOWNS, FINANCE_FORECASTS, FINANCE_METRICS, FINANCE_TABLES } from './sources/finance';
import { SALES_BREAKDOWNS, SALES_FORECASTS, SALES_METRICS, SALES_TABLES } from './sources/sales';
import {
  INVENTORY_BREAKDOWNS,
  INVENTORY_METRICS,
  INVENTORY_TABLES,
  OPERATIONS_BREAKDOWNS,
  OPERATIONS_FORECASTS,
  OPERATIONS_METRICS,
  OPERATIONS_TABLES,
  PROCUREMENT_BREAKDOWNS,
  PROCUREMENT_FORECASTS,
  PROCUREMENT_METRICS,
  PROCUREMENT_TABLES,
  PROJECT_BREAKDOWNS,
  PROJECT_METRICS,
  PROJECT_TABLES,
} from './sources/operations';
import { MARKETING_BREAKDOWNS, MARKETING_FORECASTS, MARKETING_METRICS, MARKETING_TABLES } from './sources/marketing';

export const TRACKED_KPI_PREFIX = 'kpi:';

export const STATIC_SOURCES: SourceDef[] = [
  ...FINANCE_METRICS,
  ...FINANCE_BREAKDOWNS,
  ...FINANCE_TABLES,
  ...FINANCE_FORECASTS,
  ...SALES_METRICS,
  ...SALES_BREAKDOWNS,
  ...SALES_TABLES,
  ...SALES_FORECASTS,
  ...OPERATIONS_METRICS,
  ...OPERATIONS_BREAKDOWNS,
  ...OPERATIONS_TABLES,
  ...OPERATIONS_FORECASTS,
  ...INVENTORY_METRICS,
  ...INVENTORY_BREAKDOWNS,
  ...INVENTORY_TABLES,
  ...PROCUREMENT_METRICS,
  ...PROCUREMENT_BREAKDOWNS,
  ...PROCUREMENT_TABLES,
  ...PROCUREMENT_FORECASTS,
  ...PROJECT_METRICS,
  ...PROJECT_BREAKDOWNS,
  ...PROJECT_TABLES,
  ...MARKETING_METRICS,
  ...MARKETING_BREAKDOWNS,
  ...MARKETING_TABLES,
  ...MARKETING_FORECASTS,
];

const STATIC_BY_ID = new Map<string, SourceDef>(STATIC_SOURCES.map((s) => [s.id, s]));

if (STATIC_BY_ID.size !== STATIC_SOURCES.length && import.meta.env.DEV) {
  const seen = new Set<string>();
  for (const s of STATIC_SOURCES) {
    if (seen.has(s.id)) console.error(`[intelligence] duplicate source id: ${s.id}`);
    seen.add(s.id);
  }
}

export function getStaticSource(id: string): SourceDef | undefined {
  return STATIC_BY_ID.get(id);
}

// ---------------------------------------------------------------------------
// Tracked KPIs (org-defined, stored in kpi_definitions / kpi_snapshots)
// ---------------------------------------------------------------------------

const TRACKED_KPI_UNITS: ReadonlySet<string> = new Set<Unit>([
  'currency',
  'percent',
  'count',
  'days',
  'months',
  'ratio',
  'score',
  'multiple',
  'seconds',
]);

export function trackedKpiUnit(unit: string): Unit {
  return TRACKED_KPI_UNITS.has(unit) ? (unit as Unit) : 'count';
}

export function trackedKpiSourceId(definitionId: string): string {
  return `${TRACKED_KPI_PREFIX}${definitionId}`;
}

export function trackedKpiMetric(def: KpiDefinitionLite): MetricDef {
  return {
    id: trackedKpiSourceId(def.id),
    kind: 'metric',
    label: def.label,
    group: 'Tracked KPIs',
    description: `Manually tracked KPI (${def.key}). Shows the latest recorded value in the window.`,
    module: 'intelligence',
    requires: ['kpi_tracked'],
    unit: trackedKpiUnit(def.unit),
    direction: def.direction === 'lower_is_better' ? 'down' : 'up',
    grain: 'day',
    agg: 'last',
    series: (bag) =>
      (bag.kpi_tracked?.snapshots ?? [])
        .filter((s) => s.kpi_definition_id === def.id)
        .map((s) => ({ t: s.as_of, v: s.value })),
  };
}

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------

export interface Viewer {
  role: Role | null;
  sectionAccess?: SectionAccess | null;
}

export function isDatasetPermitted(id: DatasetId, viewer: Viewer): boolean {
  return canRead(viewer.role, DATASETS[id].module, viewer.sectionAccess);
}

export function isSourcePermitted(def: SourceDef, viewer: Viewer): boolean {
  if (!canRead(viewer.role, def.module, viewer.sectionAccess)) return false;
  return def.requires.every((id) => isDatasetPermitted(id, viewer));
}

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export interface Catalog {
  sources: SourceDef[];
  byId: Map<string, SourceDef>;
  metrics: MetricDef[];
  breakdowns: BreakdownDef[];
  tables: TableDef[];
  forecasts: ForecastSourceDef[];
  metric: (id: string) => MetricDef | undefined;
}

function assemble(sources: SourceDef[]): Catalog {
  const byId = new Map(sources.map((s) => [s.id, s]));
  return {
    sources,
    byId,
    metrics: sources.filter((s): s is MetricDef => s.kind === 'metric'),
    breakdowns: sources.filter((s): s is BreakdownDef => s.kind === 'breakdown'),
    tables: sources.filter((s): s is TableDef => s.kind === 'table'),
    forecasts: sources.filter((s): s is ForecastSourceDef => s.kind === 'forecast'),
    metric: (id) => {
      const def = byId.get(id);
      return def && def.kind === 'metric' ? def : undefined;
    },
  };
}

/** Static + tracked-KPI sources the viewer may read. */
export function buildCatalog(bag: DatasetBag, viewer: Viewer): Catalog {
  const tracked = (bag.kpi_tracked?.definitions ?? []).map(trackedKpiMetric);
  const sources = [...STATIC_SOURCES, ...tracked].filter((s) => isSourcePermitted(s, viewer));
  return assemble(sources);
}

export const EMPTY_CATALOG: Catalog = assemble([]);

// ---------------------------------------------------------------------------
// Widget → source / dataset resolution
// ---------------------------------------------------------------------------

export function widgetSourceIds(widget: DashboardWidget): string[] {
  switch (widget.kind) {
    case 'metric':
      return [widget.metric];
    case 'chart':
      return [...widget.series.map((s) => s.metric), ...(widget.breakdown ? [widget.breakdown] : [])];
    case 'forecast':
      return [widget.source];
    case 'table':
      return [widget.table];
    case 'text':
      return [];
  }
}

/** Datasets needed to render the given sources, resolving ratio dependencies. */
export function requiredDatasets(sourceIds: Iterable<string>, viewer: Viewer): Set<DatasetId> {
  const out = new Set<DatasetId>();
  const visit = (id: string, depth: number) => {
    if (depth > 4) return;
    if (id.startsWith(TRACKED_KPI_PREFIX)) {
      out.add('kpi_tracked');
      return;
    }
    const def = STATIC_BY_ID.get(id);
    if (!def || !isSourcePermitted(def, viewer)) return;
    for (const ds of def.requires) out.add(ds);
    if (def.kind === 'metric' && def.ratio) {
      visit(def.ratio.num, depth + 1);
      visit(def.ratio.den, depth + 1);
    }
  };
  for (const id of sourceIds) visit(id, 0);
  return out;
}

export function sourceLabel(id: string, catalog: Catalog): string {
  return catalog.byId.get(id)?.label ?? STATIC_BY_ID.get(id)?.label ?? 'Unavailable source';
}
